import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
  upsertNotification,
} from "../../redux/notificationsSlice";

const Notifications: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  const { notifications } = useSelector(
    (state: RootState) => state.notifications
  );

  /* 🔥 START LISTENER */
  useEffect(() => {
    if (!userId) return;

    dispatch(listenToUserNotifications(userId));

    return () => {
      dispatch(stopUserNotificationsListener());
    };
  }, [dispatch, userId]);

  /* 🔴 UNREAD COUNT */
  const unreadCount = notifications.filter((n) => !n.read).length;

  /* 🔥 SORTING LOGIC */
  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      // Unread first
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }

      // Newest first within same read status
      const timeA = a.createdAt || 0;
      const timeB = b.createdAt || 0;

      return timeB - timeA;
    });
  }, [notifications]);

  const previewWords = (msg?: string) => {
    if (!msg) return "";
    return msg.split(" ").slice(0, 2).join(" ") + "...";
  };

  const formatDate = (timestamp?: number | null) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  /* 🔥 UPSERT READ */
  const openNotification = (id: string) => {
    if (!userId) return;

    dispatch(
      upsertNotification({
        userId,
        notificationId: id,
        data: { read: true },
      })
    );

    navigate(`/NotificationMessage/${id}`);
  };

  return (
    <div style={styles.wrapper}>
      {/* FIXED HEADER */}
      <div style={styles.header}>
        <div style={styles.iconWrapper}>
          <NotificationsActiveIcon style={{ color: "#0A66C2", fontSize: 28 }} />
          {unreadCount > 0 && (
            <div style={styles.badge}>{unreadCount}</div>
          )}
        </div>
        <h2 style={styles.title}>Notifications</h2>
      </div>

      {sortedNotifications.length === 0 && (
        <div style={styles.empty}>No notifications yet ✨</div>
      )}

      <div style={styles.grid}>
        {sortedNotifications.map((n, i) => {
          const isUnread = !n.read;

          return (
            <motion.div
              key={n.notificationId}
              style={styles.card(isUnread)}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => openNotification(n.notificationId)}
            >
              <div style={styles.cardTop}>
                <div style={styles.dot(isUnread)} />
                <strong style={styles.cardTitle}>{n.title}</strong>
              </div>

              <div style={styles.preview}>
                {previewWords(n.message)}
              </div>

              <div style={styles.date}>
                Sent on {formatDate(n.createdAt)}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Notifications;

/* ================= STYLES ================= */

const styles: { [key: string]: any } = {
  wrapper: {
    padding: "100px 24px 40px",
    maxWidth: 900,
    margin: "0 auto",
  },

  header: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: 70,
    background: "#111",
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    zIndex: 1000,
  },

  iconWrapper: {
    position: "relative",
    marginRight: 15,
  },

  badge: {
    position: "absolute",
    top: -6,
    right: -8,
    background: "#D32F2F",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 6px",
    borderRadius: 20,
  },

  title: {
    color: "#fff",
    fontSize: 22,
    fontWeight: 800,
  },

  empty: {
    color: "#aaa",
    marginTop: 30,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
    gap: 18,
  },

  card: (isUnread: boolean) => ({
    background: isUnread
      ? "linear-gradient(135deg, #0A66C2 0%, #111 60%)"
      : "#111",
    borderRadius: 18,
    padding: 20,
    cursor: "pointer",
    border: isUnread
      ? "1px solid #D32F2F"
      : "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 10px 25px rgba(0,0,0,0.6)",
    transition: "0.2s",
  }),

  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },

  cardTitle: {
    color: "#fff",
    fontSize: 16,
  },

  preview: {
    color: "#ccc",
    fontSize: 14,
  },

  date: {
    marginTop: 12,
    fontSize: 12,
    color: "#888",
  },

  dot: (isUnread: boolean) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: isUnread ? "#00E676" : "#555",
  }),
};
